// Typed fetch wrapper. All server calls go through this so auth, error shape and
// credential handling are uniform (02 §5).

/** RFC 7807 problem document returned by every failed request. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  trace_id?: string;
  errors?: Record<string, string>;
}

/** Error thrown for any non-2xx response, carrying the problem document. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** Field-level validation messages, keyed by field name. */
  get fieldErrors(): Record<string, string> {
    return this.problem.errors ?? {};
  }
}

/**
 * Whether the server refused because the caller's MFA is stale (02 §4.1).
 *
 * Distinguished from an ordinary 403 because the two mean opposite things to
 * the person at the keyboard: "you may not do this" is final, "prove it is
 * still you" is a step they can take. Both arrive as 403, so the problem type
 * is the only thing that tells them apart.
 */
export function isStepUpRequired(err: unknown): boolean {
  return err instanceof ApiError && err.problem.type.endsWith('/step-up-required');
}

const BASE = '/api/v1';

/**
 * The access token lives in memory only.
 *
 * localStorage would make it readable by any script on the page, turning an XSS
 * into durable session theft. In memory it dies with the tab; the httpOnly
 * refresh cookie is what survives a reload, and JavaScript cannot read that.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Idempotency key, required by the server on POSTs that create money-adjacent
   * records (02 §5). Harmless elsewhere.
   */
  idempotencyKey?: string;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, idempotencyKey, headers, ...rest } = opts;

  // FormData carries its own content type, including a boundary the browser
  // generates. Setting the header ourselves would replace it with one that has
  // no boundary, and the server would reject every upload as unparseable.
  const isForm = body instanceof FormData;

  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    // The refresh token is an httpOnly cookie; it must ride along automatically.
    credentials: 'include',
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: isForm ? body : JSON.stringify(body) } : {}),
  });

  if (res.status === 204) {
    return undefined as T;
  }

  if (!res.ok) {
    throw new ApiError(await toProblem(res));
  }

  return (await res.json()) as T;
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const parsed = (await res.json()) as Partial<Problem>;
    if (typeof parsed?.status === 'number' && typeof parsed?.title === 'string') {
      return parsed as Problem;
    }
  } catch {
    // Body was not problem+json — fall through to a synthetic problem below.
  }
  // Never surface the raw response body: on an unexpected error path it may
  // contain content we have not vetted for personal data.
  return {
    type: 'about:blank',
    title: res.statusText || 'Request failed',
    status: res.status,
  };
}

/** A file fetched from the API, with the name the server gave it. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
  headers: Headers;
}

/**
 * GET a file rather than JSON.
 *
 * A fetch rather than an `<a href>` because the access token lives in memory
 * and travels in the Authorization header. A plain link navigation carries the
 * refresh cookie but not the header, so the server would refuse it — and the
 * user would see a download that silently produced a login page.
 */
export async function download(path: string): Promise<DownloadedFile> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  if (!res.ok) {
    throw new ApiError(await toProblem(res));
  }

  return {
    blob: await res.blob(),
    filename: filenameFrom(res.headers.get('Content-Disposition')),
    headers: res.headers,
  };
}

/**
 * Hands a fetched file to the browser as a save.
 *
 * The object URL is revoked immediately after the click. Left alive it pins the
 * whole file in memory for the life of the tab, which for a year of payslips is
 * not nothing.
 */
export function saveFile(file: DownloadedFile): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Reads the filename out of a Content-Disposition header.
 *
 * Deliberately conservative: the value is used as a filename, so anything with
 * a path separator or a quote in it is discarded rather than sanitised. A
 * boring fallback name beats a clever parse.
 */
function filenameFrom(header: string | null): string {
  const fallback = 'download';
  if (!header) return fallback;

  const match = /filename="?([^";]+)"?/i.exec(header);
  const name = match?.[1]?.trim();
  if (!name || /[/\\]/.test(name)) return fallback;
  return name;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  download,
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  /**
   * Multipart POST, for the few endpoints whose payload is a file rather than
   * JSON. The FormData is passed through untouched so the browser sets the
   * content type and its boundary.
   */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
};

/** Envelope for every cursor-paginated list response. */
export interface Page<T> {
  items: T[];
  next_cursor?: string;
  has_more: boolean;
}
